import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";

import { ApiError, api, type BillingExportKind } from "../api/client";
import { Badge, Button, Card, EmptyState, PageHeader, Skeleton, TableSkeleton } from "../components/ui";
import { downloadBlob } from "../lib/download";
import { formatDate, formatMoney } from "../lib/format";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";

function iso(d: Date): string {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function monthStart(): string {
  return iso(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
}

function todayISO(): string {
  return iso(new Date());
}

const EXPORTS: Array<[BillingExportKind, string]> = [
  ["invoices", "Invoices"],
  ["payments", "Payments"],
  ["receivables", "Receivables"],
  ["credit-notes", "Credit notes"],
];

/**
 * Billing reports.
 *
 * Every total is an aggregation the server ran over the ledger; the page only
 * renders it. Exports are the same data as CSV, behind the same gates.
 */
export function BillingReportsPage() {
  const timeZone = useAccountTimeZone();
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayISO());
  const [exportError, setExportError] = useState<string | null>(null);

  const sales = useQuery({
    queryKey: ["billing", "sales", start, end],
    queryFn: ({ signal }) => api.billingSales({ start, end }, signal),
  });
  const collections = useQuery({
    queryKey: ["billing", "collections", start, end],
    queryFn: ({ signal }) => api.billingCollections({ start, end }, signal),
  });
  const creditNotes = useQuery({
    queryKey: ["billing", "credit-notes-report", start, end],
    queryFn: ({ signal }) => api.billingCreditNotesReport({ start, end }, signal),
  });
  const ageing = useQuery({
    queryKey: ["billing", "ageing"],
    queryFn: ({ signal }) => api.billingAgeing(signal),
  });
  const balances = useQuery({
    queryKey: ["billing", "customer-balances"],
    queryFn: ({ signal }) => api.billingCustomerBalances(signal),
  });
  const recurring = useQuery({
    queryKey: ["billing", "recurring-report"],
    queryFn: ({ signal }) => api.billingRecurringReport(signal),
  });

  async function download(kind: BillingExportKind) {
    setExportError(null);
    try {
      const blob = await api.billingExportCsv(kind, { start, end });
      downloadBlob(blob, `${kind}.csv`);
    } catch (error) {
      setExportError(error instanceof ApiError ? error.message : "Could not export.");
    }
  }

  const maxTrend = Math.max(
    1,
    ...(sales.data?.trend ?? []).map((point) => Number(point.invoiced?.amount ?? 0)),
    ...(sales.data?.trend ?? []).map((point) => Number(point.collected?.amount ?? 0)),
  );

  return (
    <>
      <Link
        to="/billing/dashboard"
        className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft size={14} /> Billing dashboard
      </Link>
      <PageHeader title="Billing reports" subtitle="Trends, collections, receivables and credits." />

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            From
          </span>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            aria-label="Report start"
            className="rounded-md border-0 bg-white px-2.5 py-1.5 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            To
          </span>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            aria-label="Report end"
            className="rounded-md border-0 bg-white px-2.5 py-1.5 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </label>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {EXPORTS.map(([kind, label]) => (
            <Button key={kind} onClick={() => download(kind)}>
              <Download size={14} /> {label} CSV
            </Button>
          ))}
        </div>
      </div>

      {exportError ? (
        <p role="alert" className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {exportError}
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" padded={false}>
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">Sales and collections</h2>
            <p className="mt-0.5 text-xs text-slate-500">Invoiced against collected, per month.</p>
          </div>
          {sales.isPending ? (
            <div className="p-5">
              <Skeleton className="h-40 w-full" />
            </div>
          ) : sales.isError || !sales.data ? (
            <EmptyState title="Could not load sales" body="Please try again." />
          ) : (
            <div className="space-y-4 px-5 py-4">
              {(sales.data.trend ?? []).length === 0 ? (
                <p className="text-sm text-slate-500">No activity in this window.</p>
              ) : (
                sales.data.trend.map((point) => (
                  <div key={point.period}>
                    <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
                      <span>{point.period}</span>
                      <span className="tabular-nums">
                        {formatMoney(point.invoiced)} / {formatMoney(point.collected)}
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-sky-500"
                        style={{ width: `${(Number(point.invoiced?.amount ?? 0) / maxTrend) * 100}%` }}
                      />
                    </div>
                    <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-emerald-500"
                        style={{ width: `${(Number(point.collected?.amount ?? 0) / maxTrend) * 100}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </Card>

        <Card>
          <h2 className="mb-3 text-sm font-semibold">Period totals</h2>
          {sales.data ? (
            <dl className="space-y-3 text-sm">
              <Row label="Invoiced" value={formatMoney(sales.data.totals.invoiced)} />
              <Row label="Collected (net)" value={formatMoney(sales.data.totals.collected)} />
              <Row label="Credited" value={formatMoney(sales.data.totals.credited)} />
              <Row label="Refunded" value={formatMoney(sales.data.totals.refunded)} />
              <Row label="Invoices" value={String(sales.data.totals.invoice_count)} />
            </dl>
          ) : (
            <Skeleton className="h-24 w-full" />
          )}
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card padded={false}>
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">Collections by method</h2>
          </div>
          {collections.isPending ? (
            <TableSkeleton rows={3} />
          ) : (collections.data?.by_method.length ?? 0) === 0 ? (
            <EmptyState title="No collections" body="Nothing was received in this window." />
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-2.5">Method</th>
                  <th className="px-4 py-2.5 text-right">In</th>
                  <th className="px-4 py-2.5 text-right">Out</th>
                  <th className="px-5 py-2.5 text-right">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {collections.data?.by_method.map((row) => (
                  <tr key={row.method}>
                    <td className="px-5 py-3 text-sm capitalize text-slate-700">{row.method}</td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-emerald-700">
                      {formatMoney(row.inbound)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-rose-700">
                      {formatMoney(row.outbound)}
                    </td>
                    <td className="px-5 py-3 text-right text-sm font-medium tabular-nums text-slate-900">
                      {formatMoney(row.net)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-50">
                  <td className="px-5 py-3 text-sm font-semibold text-slate-900">Total</td>
                  <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums text-slate-900">
                    {formatMoney(collections.data?.total_inbound)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums text-slate-900">
                    {formatMoney(collections.data?.total_outbound)}
                  </td>
                  <td className="px-5 py-3 text-right text-sm font-semibold tabular-nums text-slate-900">
                    {formatMoney(collections.data?.net)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          {collections.data && collections.data.by_date.length > 0 ? (
            <div className="border-t border-slate-100 px-5 py-4">
              <h3 className="text-sm font-semibold">Collections by date</h3>
              <table className="mt-2 w-full">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                    <th className="py-2">Date</th>
                    <th className="py-2 text-right">In</th>
                    <th className="py-2 text-right">Out</th>
                    <th className="py-2 text-right">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {collections.data.by_date.map((row) => (
                    <tr key={row.date}>
                      <td className="py-2 text-sm text-slate-700">
                        {formatDate(row.date, timeZone)}
                      </td>
                      <td className="py-2 text-right text-sm tabular-nums text-emerald-700">
                        {formatMoney(row.inbound)}
                      </td>
                      <td className="py-2 text-right text-sm tabular-nums text-rose-700">
                        {formatMoney(row.outbound)}
                      </td>
                      <td className="py-2 text-right text-sm font-medium tabular-nums text-slate-900">
                        {formatMoney(row.net)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </Card>

        <Card padded={false}>
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">Receivables ageing</h2>
          </div>
          {ageing.isPending ? (
            <TableSkeleton rows={4} />
          ) : ageing.data ? (
            <ul className="divide-y divide-slate-100">
              {ageing.data.buckets.map((bucket) => (
                <li key={bucket.key} className="flex items-center justify-between px-5 py-3">
                  <span className="text-sm text-slate-700">
                    {bucket.label} <span className="text-xs text-slate-500">({bucket.count})</span>
                  </span>
                  <span className="text-sm font-medium tabular-nums text-slate-900">
                    {formatMoney(bucket.amount)}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card padded={false}>
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">Credit notes and refunds</h2>
            <span className="text-xs text-slate-500">
              {creditNotes.data ? `${creditNotes.data.count} raised` : "—"}
            </span>
          </div>
          {creditNotes.isPending ? (
            <TableSkeleton rows={3} />
          ) : (creditNotes.data?.recent.length ?? 0) === 0 ? (
            <EmptyState title="No credit notes" body="Nothing was credited in this window." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {creditNotes.data?.recent.map((note) => (
                <li key={note.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <Link
                      to={`/billing/credit-notes/${note.id}`}
                      className="text-sm font-medium text-slate-800 hover:underline"
                    >
                      {note.number || "Credit note"}
                    </Link>
                    <p className="truncate text-xs text-slate-500">
                      {note.contact_name || "Unknown"} · {formatDate(note.issue_date, timeZone)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium tabular-nums text-rose-700">
                      −{formatMoney(note.total_amount)}
                    </p>
                    <p className="text-xs text-slate-500">
                      refunded {formatMoney(note.amount_refunded)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card padded={false}>
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">Customer balances</h2>
          </div>
          {balances.isPending ? (
            <TableSkeleton rows={3} />
          ) : (balances.data?.results.length ?? 0) === 0 ? (
            <EmptyState title="Nothing outstanding" body="Every invoice is settled." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {balances.data?.results.map((row) => (
                <li key={row.contact} className="flex items-center justify-between px-5 py-3">
                  <Link
                    to={`/customers/contacts/${row.contact}`}
                    className="text-sm text-slate-700 hover:underline"
                  >
                    {row.contact_name || "Unknown"}
                  </Link>
                  <span className="text-sm font-medium tabular-nums text-slate-900">
                    {formatMoney(row.balance)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="mt-6" padded={false}>
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 className="text-sm font-semibold">Recurring generation</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Successful and failed automatic invoice runs.
          </p>
        </div>
        {recurring.isPending ? (
          <TableSkeleton rows={3} />
        ) : recurring.data ? (
          <>
            <div className="grid grid-cols-2 gap-2 px-5 py-4 sm:grid-cols-6">
              <MiniStat label="Active" value={recurring.data.schedules.active} />
              <MiniStat label="Paused" value={recurring.data.schedules.paused} />
              <MiniStat label="Completed" value={recurring.data.schedules.completed} />
              <MiniStat label="Generated" value={recurring.data.runs.generated} />
              <MiniStat label="Failed" value={recurring.data.runs.failed} />
              <MiniStat label="Skipped" value={recurring.data.runs.skipped} />
            </div>
            {recurring.data.recent.length === 0 ? (
              <EmptyState title="Nothing generated yet" body="Runs appear here as schedules fire." />
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-2.5">Schedule</th>
                    <th className="px-4 py-2.5">Run date</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Invoice</th>
                    <th className="px-5 py-2.5">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recurring.data.recent.map((run, index) => (
                    <tr key={`${run.schedule}-${run.run_date}-${index}`}>
                      <td className="px-5 py-3 text-sm">
                        <Link
                          to={`/billing/recurring/${run.schedule}`}
                          className="text-slate-700 hover:underline"
                        >
                          {run.schedule_name || "Schedule"}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                        {formatDate(run.run_date, timeZone)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          tone={
                            run.status === "generated"
                              ? "emerald"
                              : run.status === "failed"
                                ? "rose"
                                : "slate"
                          }
                        >
                          <span className="capitalize">{run.status}</span>
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums">
                        {run.invoice ? (
                          <Link
                            to={`/billing/invoices/${run.invoice}`}
                            className="text-slate-700 hover:underline"
                          >
                            {run.invoice_number || "Invoice"}
                          </Link>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-500">{run.detail || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        ) : null}
      </Card>
    </>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2 text-center">
      <p className="text-lg font-semibold tabular-nums text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium tabular-nums text-slate-800">{value}</dd>
    </div>
  );
}
